import { useSearchStore } from '@nuxt-monorepo/base/app/store/search';

// Type for runtime config
interface RuntimeConfig {
    PROPEL_LOUNGES_URL?: string;
    PROPEL_PARKING_URL?: string;
}

// Safely get runtime config
const getSafeRuntimeConfig = (): RuntimeConfig => {
    try {
        return useRuntimeConfig().public;
    } catch {
        return {};
    }
};

// Export redirect function
export const redirectToParkingPropel = () => {
    const config = getSafeRuntimeConfig();
    const route = useRoute();
    const searchStore = useSearchStore();
    const propelProductSKU = inject<string>('propelProductSKU', '');

    // Safely access store properties with fallback values
    const entryDate = formatDateToPropel(searchStore.dates?.start?.date || new Date());
    const entryTime = searchStore.dates?.start?.time ?? '12:00'; // Default time if not specified
    const exitDate = formatDateToPropel(searchStore.dates?.end?.date || new Date());
    const exitTime = searchStore.dates?.end?.time ?? '12:00'; // Default time if not specified
    // @ts-expect-error code is not in the store
    const code = (searchStore.destination?.code || '').toUpperCase();
    const terminal = searchStore.destination?.terminal || 'dontKnow';
    const discount = searchStore.promo ? `&discount=${searchStore.promo}` : '';
    const product = propelProductSKU ? `&product=${propelProductSKU}` : '';
    const groups = '%7B"adult"%3A1%7D'; // default 1 adult - {adult:1}

    const query = route.query;
    let marketingChannels = '';
    if ('utm_source' in query) {
        marketingChannels += `&utmSource=${query['utm_source']}`;
    }
    if ('utm_medium' in query) {
        marketingChannels += `&utmMedium=${query['utm_medium']}`;
    }
    if ('clickref' in query) {
        marketingChannels += `&clickref=${query['clickref']}`;
    }

    window.location.href = `${config.PROPEL_PARKING_URL}search/?entryDate=${entryDate}&entryTime=${entryTime}&exitDate=${exitDate}&exitTime=${exitTime}&airport=${code}&terminal=${terminal}&groups=${groups}${discount}${product}${marketingChannels}`;
};

export const redirectToLoungePropel = () => {
    const config = getSafeRuntimeConfig();
    const route = useRoute();
    const searchStore = useSearchStore();
    const propelProductSKU = inject<string>('propelProductSKU', '');

    // Safely access store properties with fallback values
    const entryDate = formatDateToPropel(searchStore.dates?.start?.date || new Date());
    const entryTime = searchStore.dates?.start?.time || searchStore.dates?.start?.timeRange?.split('-')?.[0] || '12:00';
    // @ts-expect-error code is not in the store
    const code = (searchStore.destination?.code || '').toUpperCase();
    const terminal = searchStore.destination?.terminal || 'dontKnow';
    const product = propelProductSKU ? `&product=${propelProductSKU}` : '';
    const promo = searchStore.promo || '';

    const groups = {
        // @ts-ignore
        adult: searchStore.guests.adults || searchStore.guests?.guests?.adult?.value,
        // @ts-ignore
        child: searchStore.guests.children || searchStore.guests?.guests?.children?.value,
        // @ts-ignore
        infant: searchStore.guests.infants || searchStore.guests?.guests?.infants?.value,
    };

    const query = route.query;
    let marketingChannels = '';
    if ('utm_source' in query) {
        marketingChannels += `&utmSource=${query['utm_source']}`;
    }
    if ('utm_medium' in query) {
        marketingChannels += `&utmMedium=${query['utm_medium']}`;
    }
    if ('clickref' in query) {
        marketingChannels += `&clickref=${query['clickref']}`;
    }

    window.location.href = `${config.PROPEL_LOUNGES_URL}search/?date=${entryDate}&time=${entryTime}&exitDate=&exitTime=&terminal=${terminal}&airline=&swapped=false$&promo=${promo}&groups=${JSON.stringify(groups)}&airport=${code}${product}${marketingChannels}`;
};

// Utility function to format date
const formatDateToPropel = (date: string | Date | undefined) => {
    // If no date is provided, use current date
    if (!date) return new Date().toISOString().slice(0, 10);

    // Ensure we have a valid Date object
    const validDate = date instanceof Date ? date : new Date(date);

    // Check if the date is valid
    if (isNaN(validDate.getTime())) {
        console.warn('Invalid date provided, using current date');
        return new Date().toISOString().slice(0, 10);
    }

    // Adjust for timezone offset to prevent potential RangeError
    const timezoneOffset = validDate.getTimezoneOffset();
    const adjustedDate = new Date(validDate.getTime() - timezoneOffset * 60000);

    return adjustedDate.toISOString().slice(0, 10);
};

// Utility function to find date position
const findDatePosition = (dateValues: any, inputDate: string) => {
    const datesCopy = { ...dateValues };
    // remove default value for sorting only dates
    delete datesCopy?.default;
    // for format "YYYY-MM-DD", the normal JS sort() function works correctly
    const datesSorted = Object.keys(datesCopy).sort();

    if (inputDate < datesSorted[0] || datesSorted.length === 0) {
        return 'default';
    }

    for (let i = 0; i < datesSorted.length - 1; i++) {
        if (inputDate >= datesSorted[i] && inputDate < datesSorted[i + 1]) {
            return datesSorted[i];
        }
    }

    return datesSorted[datesSorted.length - 1];
};
